/**
 * Simple CSV parser utility
 */
import { isBillableTransactionStatus } from '../config/billing';

export const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim());
  const transactions = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    if (values.length === headers.length) {
      const transaction = {};
      headers.forEach((header, index) => {
        transaction[header] = values[index];
      });
      transactions.push(transaction);
    }
  }
  
  return transactions;
};

/**
 * Parse date and time from CSV format
 */
export const parseTransactionDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  try {
    const dateTimeStr = `${dateStr} ${timeStr}`;
    return new Date(dateTimeStr);
  } catch (e) {
    return null;
  }
};

/**
 * Format date for display
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return 'N/A';
  }
};

/**
 * Format date and time for display
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (e) {
    return 'N/A';
  }
};

/**
 * Format currency
 */
export const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

/**
 * Group transactions by month
 */
export const groupByMonth = (transactions) => {
  const grouped = {};
  transactions.forEach(txn => {
    const date = parseTransactionDate(txn.date, txn.time);
    if (date) {
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          month: monthName,
          monthKey,
          transactions: [],
          total: 0
        };
      }
      grouped[monthKey].transactions.push(txn);
      grouped[monthKey].total += parseFloat(txn.amount) || 0;
    }
  });
  return grouped;
};

/**
 * Filter transactions by date range
 */
export const filterByDateRange = (transactions, startDate, endDate) => {
  return transactions.filter(txn => {
    const date = parseTransactionDate(txn.date, txn.time);
    if (!date) return false;
    if (startDate && date < startDate) return false;
    if (endDate && date > endDate) return false;
    return true;
  });
};

/**
 * Filter transactions by status
 */
export const filterByStatus = (transactions, status) => {
  if (!status || status === 'all') return transactions;
  if (status.toLowerCase() === 'completed') {
    return transactions.filter((txn) => isBillableTransactionStatus(txn.status));
  }
  if (status.toLowerCase() === 'unaccepted') {
    // Unaccepted = anything that isn't billable (covers legacy pending/failed rows too)
    return transactions.filter((txn) => !isBillableTransactionStatus(txn.status));
  }
  return transactions.filter(
    (txn) => txn.status?.toLowerCase() === status.toLowerCase()
  );
};

